import { useState, useRef, useEffect } from "react";
import { X, Upload, Loader2 } from "lucide-react";
import { uploadService } from "@/services/upload.service";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";

interface ImageUploadProps {
  currentImage?: string | null;
  onImageChange: (url: string | null) => void;
  disabled?: boolean;
  /** Função customizada de upload. Se fornecida, substitui o uploadService padrão. */
  uploadFn?: (file: File) => Promise<string>;
  /** Função customizada de delete. Se fornecida, substitui o deleteImagemViaBackend padrão. */
  deleteFn?: () => Promise<void>;
  /** Callback com a data URL local gerada pelo FileReader (para preview imediato no pai). */
  onPreviewChange?: (dataUrl: string | null) => void;
}

export function ImageUpload({
  currentImage,
  onImageChange,
  disabled = false,
  uploadFn,
  deleteFn,
  onPreviewChange,
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Indica que existe um preview local (data URL do FileReader) que não deve ser
  // sobrescrito quando o pai atualizar currentImage com a URL do backend (privada).
  const hasLocalPreviewRef = useRef(false);

  useEffect(() => {
    // Só sincroniza do pai se não tivermos um preview local ativo
    if (!hasLocalPreviewRef.current) {
      setPreview(currentImage || null);
    }
  }, [currentImage]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;


    const validation = uploadService.validateImage(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }


    const reader = new FileReader();
    reader.onloadend = () => {
      hasLocalPreviewRef.current = true;
      const dataUrl = reader.result as string;
      setPreview(dataUrl);
      onPreviewChange?.(dataUrl);
    };
    reader.readAsDataURL(file);


    try {
      setUploading(true);

      const url = uploadFn
        ? await uploadFn(file)
        : await uploadService.uploadProjetoImagemViaBackend(file);

      // Mantém o preview local (data URL do FileReader) para exibição imediata,
      // pois a URL do storage pode ser privada e requer signed URL
      onImageChange(url);
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro ao fazer upload da imagem";
      alert(errorMessage);
      setPreview(currentImage || null);
      

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    hasLocalPreviewRef.current = false;
    onPreviewChange?.(null);
    try {
      if (deleteFn) {
        await deleteFn();
      } else if (currentImage) {
        await uploadService.deleteImagemViaBackend(currentImage);
      }
    } catch (error) {
      console.error("Erro ao deletar imagem:", error);
    }

    setPreview(null);
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3">
      {preview ? (
        <div className="relative w-full max-w-md">
          <div className="relative aspect-video w-full rounded-lg border border-border overflow-hidden bg-muted">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          </div>
          {!disabled && !uploading && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 h-8 w-8 rounded-full shadow-md"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
          disabled={disabled || uploading}
          className={cn(
            "w-full max-w-md h-32 border-2 border-dashed relative",
            !disabled && !uploading && "hover:bg-accent hover:border-primary/50"
          )}
        >
          <div className="flex flex-col items-center gap-2">
            {uploading ? (
              <>
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
                <span className="text-sm text-muted-foreground">Enviando...</span>
              </>
            ) : (
              <>
                <div className="p-3 rounded-full bg-primary/10">
                  <Upload className="h-6 w-6 text-primary" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium">Clique para enviar imagem</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG, PNG, WEBP ou GIF (max 5MB)
                  </p>
                </div>
              </>
            )}
          </div>
        </Button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        disabled={disabled || uploading}
        className="sr-only"
      />
    </div>
  );
}
